import React, { useEffect, useState, useMemo } from 'react';
import AdminLayout from '../../components/AdminLayout';
import rekamMedisService from '../../services/rekam-medis-service';
import pendaftaranService from '../../services/pendaftaran-service';
import apiService from '../../services/api-service';

const fmtTanggal = (val) => {
  if (!val) return '-';
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('id-ID', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  }
  const parsed = new Date(val);
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).toLocaleDateString('id-ID', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
};

const parseResep = (str) => {
  try { return JSON.parse(str) ?? []; }
  catch { return []; }
};

const fmtResep = (str) => {
  const list = parseResep(str);
  if (!list.length) return str?.trim() || null;
  return list.map((obat, i) => {
    const dosis = [obat.dosis, obat.satuan].filter(Boolean).join(' ');
    const aturan = [obat.frekuensi, obat.waktu].filter(Boolean).join(', ');
    const baris = [`${i + 1}. ${obat.nama_obat || 'Obat'}`];
    if (dosis) baris.push(`   Dosis: ${dosis}`);
    if (aturan) baris.push(`   Aturan: ${aturan}`);
    if (obat.catatan) baris.push(`   Catatan: ${obat.catatan}`);
    return baris.join('\n');
  }).join('\n\n');
};

// ===================== MODAL FORM =====================
function ModalForm({ mode, data, onClose, onSave }) {
  const [pasienList, setPasienList] = useState([]);
  const [dokterList, setDokterList] = useState([]);
  const [form, setForm] = useState({
    pasien_id: data?.pasien_id ? String(data.pasien_id) : '',
    dokter_id: data?.dokter_id ? String(data.dokter_id) : '',
    pendaftaran_id:     data?.pendaftaran_id     ?? '',
    tanggal_kunjungan: data?.tanggal_kunjungan
      ? String(data.tanggal_kunjungan).split('T')[0]
      : '',
    keluhan_utama:      data?.keluhan_utama      ?? '',
    diagnosis:          data?.diagnosis          ?? '',
    anamnesis:          data?.anamnesis          ?? '',
    pemeriksaan_fisik:  data?.pemeriksaan_fisik  ?? '',
    hasil_laboratorium: data?.hasil_laboratorium ?? '',
    resep:              data?.resep              ?? '',
    tindakan:           data?.tindakan           ?? '',
    catatan_dokter:     data?.catatan_dokter     ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    apiService.get('/admin/pasien').then(r => setPasienList(r.data?.data || []));
    apiService.get('/admin/dokter').then(r => setDokterList(r.data?.data || []));
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: null });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        pasien_id: Number(form.pasien_id),
        dokter_id: Number(form.dokter_id),
        pendaftaran_id: form.pendaftaran_id ? Number(form.pendaftaran_id) : null,
      };

      if (mode === 'add') {
        await rekamMedisService.create(payload);
      } else {
        await rekamMedisService.update(Number(data.id), payload);
      }
      onSave();
    } catch (err) {
      setErrors(err?.response?.data?.errors || {});
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={modalHeaderStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>
              {mode === 'add' ? 'Tambah Rekam Medis' : 'Edit Rekam Medis'}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B7280' }}>
              Isi data rekam medis pasien secara rinci
            </p>
          </div>
          <button onClick={onClose} style={btnCloseStyle} type="button">✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', padding: '20px' }}>
          <div style={fieldGroup}>
            <label style={labelStyle}>Pasien <span style={{ color: '#EF4444' }}>*</span></label>
            <select name="pasien_id" value={form.pasien_id} onChange={handleChange} style={inputStyle}>
              <option value="">-- Pilih Pasien --</option>
              {pasienList.map(p => <option key={p.id} value={p.id}>{p.nama}</option>)}
            </select>
            {errors.pasien_id && <span style={errStyle}>{errors.pasien_id[0]}</span>}
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Dokter <span style={{ color: '#EF4444' }}>*</span></label>
            <select name="dokter_id" value={form.dokter_id} onChange={handleChange} style={inputStyle}>
              <option value="">-- Pilih Dokter --</option>
              {dokterList.map(d => <option key={d.id} value={d.id}>{d.nama} - {d.spesialisasi}</option>)}
            </select>
            {errors.dokter_id && <span style={errStyle}>{errors.dokter_id[0]}</span>}
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Tanggal Kunjungan <span style={{ color: '#EF4444' }}>*</span></label>
            <input type="date" name="tanggal_kunjungan" value={form.tanggal_kunjungan} onChange={handleChange} style={inputStyle} />
            {errors.tanggal_kunjungan && <span style={errStyle}>{errors.tanggal_kunjungan[0]}</span>}
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>ID Pendaftaran</label>
            <input type="number" name="pendaftaran_id" value={form.pendaftaran_id} onChange={handleChange} style={inputStyle} placeholder="Opsional" />
          </div>

          {[
            { name: 'keluhan_utama',      label: 'Keluhan Utama',    required: true },
            { name: 'diagnosis',          label: 'Diagnosis',        required: true },
            { name: 'anamnesis',          label: 'Anamnesis',          required: false },
            { name: 'pemeriksaan_fisik',  label: 'Pemeriksaan Fisik',  required: false },
            { name: 'hasil_laboratorium', label: 'Hasil Laboratorium', required: false },
            { name: 'resep',              label: 'Resep',              required: false },
            { name: 'tindakan',           label: 'Tindakan',           required: false },
            { name: 'catatan_dokter',     label: 'Catatan Dokter',     required: false },
          ].map(({ name, label, required }) => (
            <div key={name} style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>
                {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
              </label>
              <textarea name={name} value={form[name]} onChange={handleChange} style={textareaStyle} rows={2} />
              {required && errors[name] && <span style={errStyle}>{errors[name][0]}</span>}
            </div>
          ))}
        </div>

        <div style={modalFooterStyle}>
          <button onClick={onClose} style={btnSecondaryStyle}>Batal</button>
          <button onClick={handleSubmit} disabled={saving} style={btnPrimaryStyle}>
            {saving ? 'Menimpan...' : 'Simpan Data'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===================== MODAL DETAIL =====================
function ModalDetail({ data, onClose }) {
  const rows = [
    ['Pasien',              data.pasien?.nama],
    ['Dokter',              data.dokter?.nama + (data.dokter?.spesialisasi ? ` (${data.dokter.spesialisasi})` : '')],
    ['Tanggal Kunjungan',   fmtTanggal(data.tanggal_kunjungan)],
    ['Keluhan Utama',       data.keluhan_utama],
    ['Anamnesis',           data.anamnesis],
    ['Pemeriksaan Fisik',   data.pemeriksaan_fisik ? data.pemeriksaan_fisik.replace(/===\s*VITAL SIGNS\s*===\s*/gi, '') : null],
    ['Resep Obat',          fmtResep(data.resep)],
    ['Tindakan',            data.tindakan],
    ['Catatan Dokter',      data.catatan_dokter],
  ];

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={modalHeaderStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Detail Rekam Medis</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B7280' }}>ID Rekam Medis: #{data.id}</p>
          </div>
          <button onClick={onClose} style={btnCloseStyle}>✕</button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {rows.map(([label, value], idx) => (
                  <tr key={label} style={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#F9FAFB' }}>
                    <td style={{ padding: '12px 16px', fontWeight: '600', color: '#4B5563', width: '30%', fontSize: '13px', borderBottom: '1px solid #F3F4F6', borderRight: '1px solid #E5E7EB' }}>
                      {label}
                    </td>
                    <td style={{
                      padding: '12px 16px', fontSize: '14px', color: '#111827', borderBottom: '1px solid #F3F4F6',
                      ...(label === 'Resep Obat' ? { whiteSpace: 'pre-wrap', lineHeight: 1.6, fontFamily: 'monospace', fontSize: '13px', backgroundColor: '#FFFBEB', color: '#B45309' } : {}),
                    }}>
                      {value || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={modalFooterStyle}>
          <button onClick={onClose} style={btnSecondaryStyle}>Tutup</button>
        </div>
      </div>
    </div>
  );
}

// ===================== PAGINATION BAR =====================
function PaginationBar({ currentPage, totalPages, itemsPerPage, totalItems, onPageChange, onItemsPerPageChange }) {
  if (totalItems === 0) return null;

  const startIdx = (currentPage - 1) * itemsPerPage + 1;
  const endIdx = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '1px solid #E5E7EB', flexWrap: 'wrap', gap: '12px', background: '#F9FAFB' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#6B7280' }}>
        <span>Tampilkan</span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '13px' }}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span>data per halaman (Menampilkan {startIdx}-{endIdx} dari {totalItems} data)</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{ ...btnPagingStyle, opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
        >
          &laquo; Prev
        </button>
        
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            style={{
              ...btnPagingStyle,
              backgroundColor: currentPage === page ? '#2563EB' : '#FFFFFF',
              color: currentPage === page ? '#FFFFFF' : '#374151',
              fontWeight: currentPage === page ? '700' : '500',
            }}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{ ...btnPagingStyle, opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1, cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer' }}
        >
          Next &raquo;
        </button>
      </div>
    </div>
  );
}

// ===================== MAIN PAGE =====================
export default function RekamMedis() {
  const [activeTab, setActiveTab] = useState('rekamMedis');

  const [rekamMedisData, setRekamMedisData] = useState([]);
  const [antrianData,    setAntrianData]    = useState([]);
  const [riwayatData,    setRiwayatData]    = useState([]);

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState('');

  // State Paginasi
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [modalForm,   setModalForm]   = useState(null);
  const [modalDetail, setModalDetail] = useState(null);
  const [deleteId,    setDeleteId]    = useState(null);
  const [deleting,    setDeleting]    = useState(false);

  const fetchData = async (tab = activeTab) => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'rekamMedis') {
        const result = await rekamMedisService.getAll();
        const items = result.data ?? result.rekam_medis ?? result;
        setRekamMedisData([...(Array.isArray(items) ? items : [])]);
      } else if (tab === 'antrian') {
        const result = await pendaftaranService.getAll();
        const semua  = result.data || result;
        setAntrianData(semua.filter(p => ['pending', 'confirmed', 'checked_in'].includes(p.status)));
      } else if (tab === 'riwayat') {
        const result = await pendaftaranService.getAll();
        const semua  = result.data || result;
        setRiwayatData(semua.filter(p => ['completed', 'cancelled'].includes(p.status)));
      }
    } catch (err) {
      setError(err.message || 'Gagal mengambil data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(activeTab); }, [activeTab]);

  // Reset ke halaman 1 saat berpindah tab atau mengubah kata pencarian
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, search]);

  const handleDelete = async () => {
    if (deleteId === null) return;
    setDeleting(true);
    try {
      await rekamMedisService.delete(deleteId);
      setRekamMedisData(prev => prev.filter(item => item.id !== deleteId));
      setDeleteId(null);
    } catch (err) {
      alert('Gagal menghapus data');
    } finally {
      setDeleting(false);
    }
  };

  // Filter Data menurut kata kunci pencarian
  const filteredRekamMedis = useMemo(() => {
    const q = search.toLowerCase();
    return rekamMedisData.filter(item =>
      item.pasien?.nama?.toLowerCase().includes(q) ||
      item.dokter?.nama?.toLowerCase().includes(q) ||
      item.dokter?.spesialisasi?.toLowerCase().includes(q)
    );
  }, [rekamMedisData, search]);

  const filteredAntrian = useMemo(() => {
    const q = search.toLowerCase();
    return antrianData.filter(item =>
      item.pasien?.nama?.toLowerCase().includes(q) ||
      item.dokter?.nama?.toLowerCase().includes(q) ||
      item.no_antrian?.toLowerCase().includes(q)
    );
  }, [antrianData, search]);

  const filteredRiwayat = useMemo(() => {
    const q = search.toLowerCase();
    return riwayatData.filter(item =>
      item.pasien?.nama?.toLowerCase().includes(q) ||
      item.dokter?.nama?.toLowerCase().includes(q) ||
      item.no_antrian?.toLowerCase().includes(q)
    );
  }, [riwayatData, search]);

  // Data terpotong sesuai halaman aktif
  const paginatedRekamMedis = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRekamMedis.slice(start, start + itemsPerPage);
  }, [filteredRekamMedis, currentPage, itemsPerPage]);

  const paginatedAntrian = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAntrian.slice(start, start + itemsPerPage);
  }, [filteredAntrian, currentPage, itemsPerPage]);

  const paginatedRiwayat = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRiwayat.slice(start, start + itemsPerPage);
  }, [filteredRiwayat, currentPage, itemsPerPage]);

  const statusBadge = (status) => {
    const map = {
      pending:    { label: 'Menunggu',     bg: '#FEF3C7', color: '#92400E' },
      confirmed:  { label: 'Dikonfirmasi', bg: '#DBEAFE', color: '#1E40AF' },
      checked_in: { label: 'Hadir',        bg: '#D1FAE5', color: '#065F46' },
      completed:  { label: 'Selesai',      bg: '#E0E7FF', color: '#3730A3' },
      cancelled:  { label: 'Dibatalkan',   bg: '#FEE2E2', color: '#991B1B' },
    };
    const s = map[status] || { label: status, bg: '#F3F4F6', color: '#374151' };
    return (
      <span style={{ backgroundColor: s.bg, color: s.color, padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
        {s.label}
      </span>
    );
  };

  return (
    <AdminLayout title="">
      {/* HEADER SECTION */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#111827', margin: 0 }}>Manajemen Rekam Medis</h1>
          <p style={{ color: '#6B7280', margin: '4px 0 0', fontSize: '14px' }}>Pusat pengelolaan rekam medis dan histori kunjungan pasien</p>
        </div>
        {activeTab === 'rekamMedis' && (
          <button onClick={() => setModalForm({ mode: 'add', data: null })} style={btnPrimaryStyle}>
            + Tambah Rekam Medis
          </button>
        )}
      </div>

      {/* STATS CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={cardStatStyle}>
          <span style={{ fontSize: '12px', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase' }}>Total Rekam Medis</span>
          <div style={{ fontSize: '28px', fontWeight: '800', color: '#2563EB', marginTop: '4px' }}>{rekamMedisData.length}</div>
        </div>
        <div style={cardStatStyle}>
          <span style={{ fontSize: '12px', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase' }}>Antrian Aktif</span>
          <div style={{ fontSize: '28px', fontWeight: '800', color: '#D97706', marginTop: '4px' }}>{antrianData.length}</div>
        </div>
        <div style={cardStatStyle}>
          <span style={{ fontSize: '12px', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase' }}>Riwayat Selesai</span>
          <div style={{ fontSize: '28px', fontWeight: '800', color: '#059669', marginTop: '4px' }}>{riwayatData.length}</div>
        </div>
      </div>

      {/* MODALS */}
      {modalForm && (
        <ModalForm
          mode={modalForm.mode}
          data={modalForm.data}
          onClose={() => setModalForm(null)}
          onSave={async () => {
            setModalForm(null);
            setTimeout(async () => { await fetchData('rekamMedis'); }, 300);
          }}
        />
      )}

      {modalDetail && (
        <ModalDetail data={modalDetail} onClose={() => setModalDetail(null)} />
      )}

      {deleteId !== null && (
        <div style={overlayStyle}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '360px', textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ margin: '0 0 8px', color: '#111827' }}>Hapus Rekam Medis?</h3>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 20px' }}>Tindakan ini permanen dan data tidak dapat dikembalikan.</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button onClick={() => setDeleteId(null)} style={btnSecondaryStyle}>Batal</button>
              <button onClick={handleDelete} disabled={deleting} style={{ ...btnPrimaryStyle, backgroundColor: '#EF4444' }}>
                {deleting ? 'Menghapus...' : 'Ya, Hapus'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TABS & SEARCH BAR */}
      <div style={panelContainerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E5E7EB', padding: '0 20px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { key: 'rekamMedis', label: 'Rekam Medis' },
              { key: 'antrian',    label: 'Antrian Aktif' },
              { key: 'riwayat',    label: 'Riwayat Kunjungan' },
            ].map(tab => (
              <button key={tab.key} onClick={() => { setActiveTab(tab.key); setSearch(''); }} style={{
                padding: '16px 16px', border: 'none', cursor: 'pointer',
                fontWeight: activeTab === tab.key ? '700' : '500',
                borderBottom: activeTab === tab.key ? '3px solid #2563EB' : '3px solid transparent',
                backgroundColor: 'transparent',
                color: activeTab === tab.key ? '#2563EB' : '#6B7280',
                fontSize: '14px',
              }}>
                {tab.label}
              </button>
            ))}
          </div>

          <div style={{ padding: '8px 0' }}>
            <input
              type="text"
              placeholder="Cari pasien / dokter..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...inputStyle, width: '240px', padding: '8px 12px', fontSize: '13px' }}
            />
          </div>
        </div>

        {loading && <p style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>⏳ Memuat data...</p>}
        {error   && <div style={{ color: '#DC2626', backgroundColor: '#FEE2E2', padding: '12px 16px', margin: '20px', borderRadius: '8px' }}>Terjadi kesalahan: {error}</div>}

        {/* TAB 1: REKAM MEDIS */}
        {!loading && !error && activeTab === 'rekamMedis' && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>No</th>
                    <th style={thStyle}>Pasien</th>
                    <th style={thStyle}>Dokter</th>
                    <th style={thStyle}>Spesialisasi</th>
                    <th style={thStyle}>Tanggal Kunjungan</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRekamMedis.length === 0 ? (
                    <tr><td colSpan="6" style={emptyTdStyle}>Tidak ada data rekam medis ditemukan</td></tr>
                  ) : (
                    paginatedRekamMedis.map((item, index) => (
                      <tr key={item.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={tdStyle}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: '600', color: '#111827' }}>{item.pasien?.nama ?? '-'}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>{item.pasien?.no_pendaftaran ?? ''}</div>
                        </td>
                        <td style={tdStyle}>{item.dokter?.nama ?? '-'}</td>
                        <td style={tdStyle}>
                          <span style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                            {item.dokter?.spesialisasi ?? '-'}
                          </span>
                        </td>
                        <td style={tdStyle}>{fmtTanggal(item.tanggal_kunjungan)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '6px' }}>
                            <button onClick={() => setModalDetail(item)} style={btnActionStyle('#2563EB')}>Detail</button>
                            <button onClick={() => setModalForm({ mode: 'edit', data: item })} style={btnActionStyle('#D97706')}>Edit</button>
                            <button onClick={() => setDeleteId(item.id)} style={btnActionStyle('#EF4444')}>Hapus</button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <PaginationBar
              currentPage={currentPage}
              totalPages={Math.ceil(filteredRekamMedis.length / itemsPerPage)}
              itemsPerPage={itemsPerPage}
              totalItems={filteredRekamMedis.length}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
            />
          </>
        )}

        {/* TAB 2: ANTRIAN */}
        {!loading && !error && activeTab === 'antrian' && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>No Antrian</th>
                    <th style={thStyle}>Pasien</th>
                    <th style={thStyle}>Dokter</th>
                    <th style={thStyle}>Tanggal</th>
                    <th style={thStyle}>Jam Kunjungan</th>
                    <th style={thStyle}>Keluhan</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAntrian.length === 0 ? (
                    <tr><td colSpan="7" style={emptyTdStyle}>Tidak ada antrian aktif saat ini</td></tr>
                  ) : (
                    paginatedAntrian.map((item) => (
                      <tr key={item.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ ...tdStyle, fontWeight: '700', color: '#2563EB' }}>{item.no_antrian ?? '-'}</td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: '600', color: '#111827' }}>{item.pasien?.nama ?? '-'}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>{item.pasien?.no_pendaftaran ?? ''}</div>
                        </td>
                        <td style={tdStyle}>
                          <div>{item.dokter?.nama ?? '-'}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>{item.dokter?.spesialisasi ?? ''}</div>
                        </td>
                        <td style={tdStyle}>{fmtTanggal(item.tanggal_pendaftaran)}</td>
                        <td style={tdStyle}>{item.estimasi_jam_kehadiran || item.jam_kunjungan || '-'}</td>
                        <td style={tdStyle}>{item.keluhan ?? '-'}</td>
                        <td style={tdStyle}>{statusBadge(item.status)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <PaginationBar
              currentPage={currentPage}
              totalPages={Math.ceil(filteredAntrian.length / itemsPerPage)}
              itemsPerPage={itemsPerPage}
              totalItems={filteredAntrian.length}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
            />
          </>
        )}

        {/* TAB 3: RIWAYAT */}
        {!loading && !error && activeTab === 'riwayat' && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>No Antrian</th>
                    <th style={thStyle}>Pasien</th>
                    <th style={thStyle}>Dokter</th>
                    <th style={thStyle}>Tanggal</th>
                    <th style={thStyle}>Jam Kunjungan</th>
                    <th style={thStyle}>Keluhan</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRiwayat.length === 0 ? (
                    <tr><td colSpan="7" style={emptyTdStyle}>Tidak ada riwayat kunjungan</td></tr>
                  ) : (
                    paginatedRiwayat.map((item) => (
                      <tr key={item.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={tdStyle}>{item.no_antrian ?? '-'}</td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: '600', color: '#111827' }}>{item.pasien?.nama ?? '-'}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>{item.pasien?.no_pendaftaran ?? ''}</div>
                        </td>
                        <td style={tdStyle}>
                          <div>{item.dokter?.nama ?? '-'}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>{item.dokter?.spesialisasi ?? ''}</div>
                        </td>
                        <td style={tdStyle}>{fmtTanggal(item.tanggal_pendaftaran)}</td>
                        <td style={tdStyle}>{item.estimasi_jam_kehadiran || item.jam_kunjungan || '-'}</td>
                        <td style={tdStyle}>{item.keluhan ?? '-'}</td>
                        <td style={tdStyle}>{statusBadge(item.status)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <PaginationBar
              currentPage={currentPage}
              totalPages={Math.ceil(filteredRiwayat.length / itemsPerPage)}
              itemsPerPage={itemsPerPage}
              totalItems={filteredRiwayat.length}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
}

// ===================== STYLES =====================
const panelContainerStyle = { background: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' };
const cardStatStyle       = { background: '#FFFFFF', padding: '20px', borderRadius: '16px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const tableStyle          = { width: '100%', borderCollapse: 'collapse', textAlign: 'left' };
const thStyle             = { padding: '14px 20px', backgroundColor: '#F9FAFB', fontWeight: '700', fontSize: '12px', color: '#4B5563', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E5E7EB' };
const tdStyle             = { padding: '16px 20px', verticalAlign: 'middle', fontSize: '14px', color: '#374151' };
const emptyTdStyle        = { textAlign: 'center', padding: '48px 20px', color: '#9CA3AF', fontSize: '14px' };

const overlayStyle        = { position: 'fixed', inset: 0, backgroundColor: 'rgba(17, 24, 39, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle          = { background: '#fff', borderRadius: '16px', width: '720px', maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #E5E7EB' };
const modalHeaderStyle    = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderBottom: '1px solid #E5E7EB' };
const modalFooterStyle    = { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 20px', borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' };

const inputStyle          = { width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
const textareaStyle       = { ...inputStyle, resize: 'vertical', fontFamily: 'inherit' };
const labelStyle          = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' };
const fieldGroup          = { display: 'flex', flexDirection: 'column' };
const errStyle            = { color: '#EF4444', fontSize: '12px', marginTop: '4px' };

const btnPrimaryStyle     = { padding: '10px 20px', backgroundColor: '#2563EB', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' };
const btnSecondaryStyle   = { padding: '10px 20px', backgroundColor: '#FFFFFF', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' };
const btnCloseStyle       = { background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9CA3AF' };
const btnActionStyle      = (color) => ({ padding: '6px 12px', backgroundColor: color, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' });

const btnPagingStyle      = { padding: '6px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', backgroundColor: '#FFFFFF', fontSize: '13px', color: '#374151', cursor: 'pointer' };